import { type FC } from "react";

import { clsx } from "clsx";

import { BUCKET_COLOR, type BucketKey } from "./billing_palette";
import { BillableCompositionSubline } from "./processing_stats_card/composition_subline";

export type PageBucketKey = BucketKey;

export type PageBucketRow = {
  key: PageBucketKey;
  label: string;
  count: number;
  nonBillable?: boolean;
  // Server-provided per-bucket composition. When `condensedPageCount > 0`
  // the trailing column flips to a two-line stack (`{billableUnits}
  // billable` + composition sub-line) per spec §9.4.1. All three numbers
  // arrive pre-computed; no client-side derivation.
  billableUnits?: number;
  condensedPageCount?: number;
  multiplier?: number;
};

// Narrowed view of a divergent bucket — all four fields guaranteed
// concrete numbers. Returning this from a helper lets the JSX render
// without non-null assertions on the optional `PageBucketRow` props.
type DivergentBucket = {
  billableUnits: number;
  fullPageCount: number;
  condensedPageCount: number;
  condensedBillableUnits: number;
  multiplier: number;
};

function divergentBucket(row: PageBucketRow): DivergentBucket | null {
  const condensedPageCount = row.condensedPageCount ?? 0;
  if (
    condensedPageCount <= 0 ||
    row.billableUnits === undefined ||
    row.multiplier === undefined ||
    row.multiplier <= 1 ||
    row.billableUnits <= row.count
  ) {
    return null;
  }
  return {
    billableUnits: row.billableUnits,
    fullPageCount: row.count - condensedPageCount,
    condensedPageCount,
    condensedBillableUnits: condensedPageCount * row.multiplier,
    multiplier: row.multiplier,
  };
}

export const PageBucketBars: FC<{
  total: number;
  rows: PageBucketRow[];
  size?: "sm" | "md";
}> = ({ total, rows, size = "md" }) => {
  const safeTotal = Math.max(total, 1);
  const barHeight = size === "sm" ? "h-1.5" : "h-2";
  const labelWidth = size === "sm" ? "w-16 text-[10px]" : "w-36 text-xs";
  // When ANY row is divergent, widen the trailing column for all rows so
  // bar widths stay aligned across the set. Equal-case rows just get more
  // empty right-side space; divergent ones use it for the two-line stack.
  const anyDivergent = rows.some((r) => divergentBucket(r) !== null);
  const equalCountWidth = size === "sm" ? "w-5 text-[10px]" : "w-10 text-xs";
  const trailingWidth = anyDivergent ? "w-44 text-xs" : equalCountWidth;

  return (
    <div className="space-y-1.5">
      {rows.map((row) => {
        const width = (row.count / safeTotal) * 100;
        const divergent = divergentBucket(row);
        return (
          <div key={row.key} className="flex items-center gap-3">
            <div
              className={clsx(
                "flex items-center gap-1.5 text-slate-600",
                labelWidth,
              )}
            >
              {row.label}
              {row.nonBillable && (
                <span className="rounded border border-rose-200 bg-rose-50 px-1 py-px text-[9.5px] font-semibold tracking-wide text-rose-600 uppercase">
                  non-billable
                </span>
              )}
            </div>
            <div
              className={clsx(
                "flex-1 overflow-hidden rounded-full bg-slate-100",
                barHeight,
              )}
            >
              <div
                className={clsx(
                  "h-full rounded-full",
                  BUCKET_COLOR[row.key],
                  row.nonBillable && "opacity-60",
                )}
                style={{ width: `${width}%` }}
              />
            </div>
            {divergent ? (
              // Right edge shows `{billableUnits} billable` (with unit word
              // to disambiguate per Design QA correction 3) + math sub-line.
              // Section-level footnote anchors the physical-page count.
              <div
                className={clsx(
                  "flex flex-col items-end leading-tight",
                  trailingWidth,
                )}
              >
                <span className="font-medium text-slate-700">
                  {divergent.billableUnits} billable
                </span>
                <span className="text-[11px] font-medium text-slate-500">
                  <BillableCompositionSubline
                    fullPageCount={divergent.fullPageCount}
                    condensedBillableUnits={divergent.condensedBillableUnits}
                    condensedPageCount={divergent.condensedPageCount}
                    multiplier={divergent.multiplier}
                  />
                </span>
              </div>
            ) : (
              <div
                className={clsx(
                  "text-right font-medium",
                  trailingWidth,
                  row.nonBillable
                    ? "text-slate-400 line-through"
                    : "text-slate-700",
                )}
              >
                {row.count}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
